import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import LandingPage from "./pages/LandingPage";
import Onboarding from "./pages/mobile/Onboarding";
import Home from "./pages/mobile/Home";
import Trading from "./pages/mobile/Trading";
import Network from "./pages/mobile/Network";
import Funds from "./pages/mobile/Funds";
import Profile from "./pages/mobile/Profile";
import Support from "./pages/mobile/Support";
import KYC from "./pages/user/KYC";
import Deposits from "./pages/user/Deposits";
import Withdrawals from "./pages/mobile/Withdrawals";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminKYC from "./pages/admin/AdminKYC";
import AdminPerformance from "./pages/admin/AdminPerformance";
import AdminWithdrawals from "./pages/admin/AdminWithdrawals";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminDeposits from "./pages/admin/AdminDeposits";
import AdminMLMSettings from "./pages/admin/AdminMLMSettings";
import AdminSystemSettings from "./pages/admin/AdminSystemSettings";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminEarnings from "./pages/admin/AdminEarnings";
import "./App.css";
import Dashboard from "./pages/user/Dashboard";
import AdminBankDetails from "./pages/admin/AdminBankAccount";
import { Toaster } from "react-hot-toast";
import DailySalesReport from "./pages/mobile/DailySalesReport";
import MlmNetwork from "./pages/mobile/MlmNetwork";

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/home" />;
  }

  return children;
};

const OnboardingCheck = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      // Redirect based on role
      if (isAdmin) {
        navigate("/admin");
      } else {
        navigate("/home");
      }
    } else {
      // Show landing page for non-authenticated users
      navigate("/landing");
    }
  }, [isAuthenticated, isAdmin, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Routes>
        <Route path="/" element={<OnboardingCheck />} />
        <Route path="/landing" element={<Login />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Mobile User Routes */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/trading"
          element={
            <ProtectedRoute>
              <Trading />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/trading"
          element={
            <ProtectedRoute>
              <DailySalesReport />
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/network"
          element={
            <ProtectedRoute>
              <Network />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/network"
          element={
            <ProtectedRoute>
              <MlmNetwork />
            </ProtectedRoute>
          }
        />
        <Route
          path="/funds"
          element={
            <ProtectedRoute>
              <Funds />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kyc"
          element={
            <ProtectedRoute>
              <KYC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deposits"
          element={
            <ProtectedRoute>
              <Deposits />
            </ProtectedRoute>
          }
        />
        <Route
          path="/withdrawals"
          element={
            <ProtectedRoute>
              <Withdrawals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <Support />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/kyc"
          element={
            <ProtectedRoute requireAdmin>
              <AdminKYC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/earnings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminEarnings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bank-details"
          element={
            <ProtectedRoute requireAdmin>
              <AdminBankDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/performance"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPerformance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/withdrawals"
          element={
            <ProtectedRoute requireAdmin>
              <AdminWithdrawals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireAdmin>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/deposits"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDeposits />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/mlm-settings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminMLMSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSystemSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute requireAdmin>
              <AdminAuditLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSupport />
            </ProtectedRoute>
          }
        />

        {/* Redirect old routes */}
        <Route path="/dashboard" element={<Navigate to="/home" />} />
        <Route path="/dashboard-user" element={<Dashboard />} />
        <Route path="/plans" element={<Navigate to="/home" />} />
        <Route path="/wallet" element={<Navigate to="/funds" />} />
        <Route path="/arbitrage" element={<Navigate to="/trading" />} />
      </Routes>
    </Router>
  );
}

export default App;
